import { useEffect } from 'react';
import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';

import HomePage from '@/pages/HomePage';
import LoginPage from '@/pages/LoginPage';
import NotFoundPage from '@/pages/NotFoundPage';
import CrearCamadaPage from '@/pages/CrearCamadaPage';
import { EntradasAnimalesPage, EntradaAnimalesFormPage } from '@/pages/EntradasAnimalesPage';
import AsignarLotesPage from '@/pages/AsignarLotesPage';
import RegistroDiarioPage from '@/pages/registro-diario/RegistroDiarioPage';
import CargaEntregaPiensoPage from '@/pages/CargaEntregaPiensoPage';
import { AjustesPage } from '@/pages/AjustesPage';
import { AjustesNavesPage } from '@/pages/AjustesNavesPage';
import { AjustesSensoresAliasPage, AjustesSensoresPage } from '@/pages/AjustesSensoresPage';
import { AjustesTemaPage } from '@/pages/AjustesTemaPage';
import AjustesPiensoPage from '@/pages/AjustesPiensoPage';
import { CurvaEstandarPage } from '@/pages/CurvaEstandarPage';
import { DetalleNavePage } from '@/pages/DetalleNavePage';
import { RegistrarPesajePage } from '@/pages/RegistrarPesajePage';
import { CamadasPage } from '@/pages/CamadasPage';
import { CamadaDetallePage } from '@/pages/CamadaDetallePage';
import { CierreCamadaPage } from '@/pages/CierreCamadaPage';
import { CamadaPartesPage } from '@/pages/CamadaPartesPage';
import { CamadaEntregasPiensoPage } from '@/pages/CamadaEntregasPiensoPage';
import { CamadaSalidasPage } from '@/pages/CamadaSalidasPage';
import { SalidaFormPage } from '@/pages/SalidaFormPage';
import { CamadaMuestreosPage, NaveMuestreosAliasPage } from '@/pages/CamadaMuestreosPage';
import { CamadaEditarPage } from '@/pages/CamadaEditarPage';
import LiquidacionPage from '@/pages/LiquidacionPage';
import AjustesLiquidacionPage from '@/pages/AjustesLiquidacionPage';
import LiquidacionEvolucionPage from '@/pages/LiquidacionEvolucionPage';
import PiensoPage from '@/pages/PiensoPage';
import { IoTDemoPage } from '@/pages/IoTDemoPage';
import { AppLayout } from '@/components/AppLayout';
import { MasPage } from '@/pages/MasPage';
import { ToastContainer } from '@/components/Toast';
import { useAuthStore } from '@/stores/auth';
import { applyTheme, useThemeStore } from '@/stores/theme';
import { useQueryClient } from '@tanstack/react-query';
import { startOperationsSync } from '@/lib/offline/operations';
import { startPwaInstallTracking } from '@/stores/pwaInstall';
import { PwaInstallExperience } from '@/components/PwaInstall';

/**
 * Componente que protege rutas: requiere usuario en el store y token
 * válido. Si no hay token, redirige a /login con la ruta original en
 * state.from.
 */
function ProtectedLayout() {
  const user = useAuthStore((s) => s.user);
  const location = useLocation();
  const accessToken = useAuthStore((s) => s.bump);

  // Suscribirse al bump fuerza re-render cuando cambian tokens.
  void accessToken;

  if (!user) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }
  return <Outlet />;
}

/**
 * Inversa: si ya hay sesión, salta la pantalla de login.
 */
function PublicOnlyLayout() {
  const user = useAuthStore((s) => s.user);
  if (user) {
    return <Navigate to="/" replace />;
  }
  return <Outlet />;
}

function IoTDemoRoute() {
  const user = useAuthStore((s) => s.user);
  return user?.rol === 'admin' ? <IoTDemoPage /> : <Navigate to="/ajustes" replace />;
}

function App() {
  useEffect(() => startPwaInstallTracking(), []);
  const queryClient = useQueryClient();
  const ownerId = useAuthStore((s) => s.user?.id ?? null);
  // Carga el user desde localStorage al montar (idempotente).
  const loadFromStorage = useAuthStore((s) => s.loadFromStorage);
  const setUser = useAuthStore((s) => s.setUser);

  useEffect(() => {
    if (ownerId == null) return;
    return startOperationsSync(() => {
      // A confirmed write or local projection changed. Refresh shared totals;
      // queries retain account isolation and their offline read fallback.
      void queryClient.invalidateQueries();
    });
  }, [ownerId, queryClient]);

  useEffect(() => {
    const permissionsChanged = () => {
      void queryClient.resetQueries();
    };
    window.addEventListener('offline:permissions-changed', permissionsChanged);
    return () => window.removeEventListener('offline:permissions-changed', permissionsChanged);
  }, [queryClient]);

  // Aplica el tema guardado (data-theme + clase .dark) al arrancar y
  // en cada cambio (ADR-025).
  const theme = useThemeStore((s) => s.theme);
  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  useEffect(() => {
    loadFromStorage();

    // Escucha el evento 'auth:logout' disparado por api.ts cuando el
    // refresh falla; limpia el estado y deja que el guard redirija.
    function onAuthLogout() {
      setUser(null);
    }
    window.addEventListener('auth:logout', onAuthLogout);
    return () => {
      window.removeEventListener('auth:logout', onAuthLogout);
    };
  }, [loadFromStorage, setUser]);

  return (
    <div className="min-h-screen bg-background text-text-primary">
      <Routes>
        <Route element={<ProtectedLayout />}>
          <Route element={<AppLayout />}>
            <Route path="/" element={<HomePage />} />
            <Route path="/camadas" element={<CamadasPage />} />
            <Route path="/camadas/:camadaId" element={<CamadaDetallePage />} />
            <Route path="/camadas/:camadaId/cerrar" element={<CierreCamadaPage />} />
            <Route path="/camadas/:camadaId/partes" element={<CamadaPartesPage />} />
            <Route path="/camadas/:camadaId/salidas" element={<CamadaSalidasPage />} />
            <Route path="/camadas/:camadaId/salidas/nueva" element={<SalidaFormPage />} />
            <Route
              path="/camadas/:camadaId/salidas/:salidaId/editar"
              element={<SalidaFormPage />}
            />
            <Route
              path="/camadas/:camadaId/salidas/:salidaId/peso"
              element={<SalidaFormPage pesoOnly />}
            />
            <Route path="/camadas/:camadaId/entradas-animales" element={<EntradasAnimalesPage />} />
            <Route
              path="/camadas/:camadaId/entradas-animales/nueva"
              element={<EntradaAnimalesFormPage />}
            />
            <Route
              path="/camadas/:camadaId/entradas-animales/:entradaId/editar"
              element={<EntradaAnimalesFormPage />}
            />
            <Route
              path="/camadas/:camadaId/entregas-pienso"
              element={<CamadaEntregasPiensoPage />}
            />
            <Route
              path="/camadas/:camadaId/lotes/:loteId/muestreos"
              element={<CamadaMuestreosPage />}
            />
            <Route path="/naves/:naveId/muestreos" element={<NaveMuestreosAliasPage />} />
            <Route path="/camadas/:camadaId/editar" element={<CamadaEditarPage />} />
            <Route path="/pienso" element={<PiensoPage />} />
            <Route path="/liquidacion/:camadaId?" element={<LiquidacionPage />} />
            <Route path="/liquidacion/:camadaId/evolucion" element={<LiquidacionEvolucionPage />} />
            <Route path="/ajustes" element={<AjustesPage />} />
            <Route path="/mas" element={<MasPage />} />
            <Route path="/ajustes/tema" element={<AjustesTemaPage />} />
            <Route path="/ajustes/pienso" element={<AjustesPiensoPage />} />
            <Route path="/ajustes/liquidacion" element={<AjustesLiquidacionPage />} />
            <Route path="/ajustes/naves" element={<AjustesNavesPage />} />
            <Route path="/ajustes/naves/nueva" element={<AjustesNavesPage creating />} />
            <Route path="/ajustes/naves/:naveId" element={<AjustesNavesPage />} />
            <Route path="/ajustes/naves/:naveId/sensores" element={<AjustesSensoresPage />} />
            <Route path="/ajustes/sensores" element={<AjustesSensoresAliasPage />} />
            <Route path="/ajustes/curva" element={<CurvaEstandarPage />} />
            {import.meta.env.DEV && <Route path="/demo/iot" element={<IoTDemoRoute />} />}
            <Route path="/naves/:naveId" element={<DetalleNavePage />} />
            <Route path="/naves/:naveId/pesajes/nuevo" element={<RegistrarPesajePage />} />
            <Route path="/camadas/nueva" element={<CrearCamadaPage />} />
            <Route path="/camadas/:camadaId/asignar-lotes" element={<AsignarLotesPage />} />
            <Route path="/camadas/:camadaId/registro-diario" element={<RegistroDiarioPage />} />
            <Route
              path="/camadas/:camadaId/entregas-pienso/nueva"
              element={<CargaEntregaPiensoPage />}
            />
          </Route>
        </Route>
        <Route element={<PublicOnlyLayout />}>
          <Route path="/login" element={<LoginPage />} />
        </Route>
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      <ToastContainer />
      <PwaInstallExperience />
    </div>
  );
}

export default App;
